
import React from 'react';
import { Edit, Trash2, Eye, BarChart3 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Category } from '@/types/category';

interface CategoryCardProps {
    category: Category;
    onEdit: (category: Category) => void;
    onDelete: (categoryId: string) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onEdit, onDelete }) => {
    return (
        <Card className="group relative overflow-hidden bg-white hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border-0 shadow-lg">
            {/* Gradient background */}
            <div 
                className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500"
                style={{ background: `linear-gradient(135deg, ${category.color}, ${category.color}80)` }}
            ></div>

            <div className="relative p-6">
                {/* Header with icon and actions */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div
                            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                            style={{ backgroundColor: category.color }}
                        >
                            {category.icon || '📱'}
                        </div>
                        <div>
                            <h3 className="font-bold text-xl text-gray-900 group-hover:text-gray-800 transition-colors">
                                {category.name}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                    /{category.slug}
                                </Badge>
                                {typeof category.games_count === 'number' && (
                                    <Badge variant="outline" className="text-xs">
                                        <BarChart3 className="w-3 h-3 mr-1" />
                                        {category.games_count} games
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onEdit(category)}
                            className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        >
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDelete(category.id)}
                            className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Description */}
                {category.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {category.description}
                    </p>
                )}

                {/* Footer with metadata */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <Eye className="w-3 h-3" />
                        <span>Order: {category.order_index || 0}</span>
                    </div>
                    
                    <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: category.color }}
                        title={`Color: ${category.color}`}
                    ></div>
                </div>

                {/* Animated accent line */}
                <div 
                    className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
                    style={{ backgroundColor: category.color }}
                ></div>
            </div>
        </Card>
    );
};

export default CategoryCard;
